import { useMemo, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faLayerGroup,
  faImage,
  faTag,
  faEye,
  faArchive,
  faClock,
  faChevronUp,
  faChevronDown,
  faTrash,
  faUtensils,
  faBan,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import AddItemModal from '../modals/AddItemModal';
import EditItemModal from '../modals/EditItemModal';
import UploadImageModal from '../modals/UploadImageModal';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminMetricStrip from '../shared/AdminMetricStrip';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/route_pages/Items.css';
import { addItem, updateItem, reorderItems, reorderCategories, deleteItem, updateItemAvailability } from '../../utils/menuService';
import { useVenueId } from '../../hooks/useUserData';
import { uploadImageWithCleanup } from '../../utils/imageService';

const Items = () => {
  const venueId = useVenueId();
  const { menus, categories, setCategories, items, setItems, areas } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState('active');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [notice, setNotice] = useState(null);

  const venueMenu = useMemo(() => menus[0] || null, [menus]);

  const menuCategories = useMemo(() => {
    if (!venueMenu) {
      return [];
    }

    return categories
      .filter((category) => category.menuId === venueMenu.id && !category.isSystemCategory)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [categories, venueMenu]);

  const allMenuCategories = useMemo(() => {
    if (!venueMenu) {
      return [];
    }

    return categories.filter((category) => category.menuId === venueMenu.id);
  }, [categories, venueMenu]);

  const uncategorizedCategory = useMemo(() => {
    if (!venueMenu) {
      return null;
    }

    return categories.find(
      (category) => category.menuId === venueMenu.id && category.isSystemCategory && category.name === 'Kategorilendirilmemiş',
    );
  }, [categories, venueMenu]);

  const menuItems = useMemo(() => {
    if (!venueMenu) {
      return [];
    }

    return items.filter((item) => item.menuId === venueMenu.id);
  }, [items, venueMenu]);

  const filteredItems = useMemo(
    () =>
      menuItems
        .filter((item) => item.status === activeStatusFilter)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [activeStatusFilter, menuItems],
  );

  const getItemCountByStatus = (status) => menuItems.filter((item) => item.status === status).length;
  const getUnavailableActiveCount = () => menuItems.filter((item) => item.status === 'active' && item.isAvailable === false).length;

  const handleItemAdded = async (newItem) => {
    try {
      setLoading(true);
      setNotice(null);
      const addedItem = await addItem(venueId, newItem, getAccessTokenSilently);
      setItems([...items, addedItem]);
      setCurrentItem(addedItem);
      setIsAddModalVisible(false);
      setIsUploadModalVisible(true);
    } catch (error) {
      console.error('Failed to add item:', error);
      setNotice({
        tone: 'error',
        title: 'Ürün eklenemedi',
        message: 'Ürün eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleItemUpdated = async (updatedItem, options = {}) => {
    try {
      setLoading(true);
      setNotice(null);
      const updated = await updateItem(venueId, updatedItem, getAccessTokenSilently);
      setItems((previousItems) => previousItems.map((item) => (item.id === updated.id ? updated : item)));
      if (!options.keepModalOpen) {
        setIsEditModalVisible(false);
      }
      if (!options.suppressNotice) {
        setNotice({
          tone: 'success',
          title: 'Ürün güncellendi',
          message: `${updated.name} için değişiklikler kaydedildi.`,
        });
      }
      return updated;
    } catch (error) {
      console.error('Failed to update item:', error);
      setNotice({
        tone: 'error',
        title: 'Ürün güncellenemedi',
        message: 'Ürün bilgileri kaydedilemedi.',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setNotice(null);
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) {
      return;
    }

    try {
      setLoading(true);
      setNotice(null);
      await deleteItem(venueId, itemToDelete.id, getAccessTokenSilently);
      setItems(items.filter((item) => item.id !== itemToDelete.id));
      setShowDeleteConfirmation(false);
      setNotice({
        tone: 'success',
        title: 'Ürün silindi',
        message: `${itemToDelete.name} menüden kaldırıldı.`,
      });
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete item:', error);
      setNotice({
        tone: 'error',
        title: 'Ürün silinemedi',
        message: 'Ürün silinirken bir sorun oluştu.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      setLoading(true);
      setNotice(null);
      const updated = await updateItem(venueId, { ...item, status: newStatus }, getAccessTokenSilently);
      setItems((previousItems) => previousItems.map((entry) => (entry.id === updated.id ? updated : entry)));
      setNotice({
        tone: 'success',
        title: newStatus === 'archived' ? 'Ürün arşivlendi' : 'Ürün güncellendi',
        message: `${updated.name} artık ${getStatusText(newStatus).toLowerCase()} durumda.`,
      });
    } catch (error) {
      console.error('Failed to update item status:', error);
      setNotice({
        tone: 'error',
        title: 'Durum güncellenemedi',
        message: 'Ürün durumu değiştirilemedi.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvailabilityToggle = async (item, isAvailable) => {
    try {
      setLoading(true);
      setNotice(null);
      const updated = await updateItemAvailability(venueId, item.id, isAvailable, getAccessTokenSilently);
      setItems((previousItems) => previousItems.map((entry) => (entry.id === updated.id ? updated : entry)));
      setNotice({
        tone: 'success',
        title: isAvailable ? 'Ürün yeniden satışa açıldı' : 'Ürün geçici olarak kapatıldı',
        message: isAvailable
          ? `${updated.name} yeniden siparişe açıldı.`
          : `${updated.name} artık siparişe kapalı.`,
      });
    } catch (error) {
      console.error('Failed to update item availability:', error);
      setNotice({
        tone: 'error',
        title: 'Sipariş durumu güncellenemedi',
        message: 'Ürünün satış durumu güncellenemedi.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveClick = (item) => {
    setNotice(null);
    setItemToArchive(item);
    setShowArchiveConfirmation(true);
  };

  const handleConfirmArchive = async () => {
    if (itemToArchive) {
      await handleStatusChange(itemToArchive, 'archived');
      setShowArchiveConfirmation(false);
      setItemToArchive(null);
    }
  };

  const handleMoveItem = async (item, direction, categoryItems) => {
    const currentIndex = categoryItems.findIndex((entry) => entry.id === item.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= categoryItems.length) {
      return;
    }

    try {
      setLoading(true);
      const reorderedItems = Array.from(categoryItems);
      [reorderedItems[currentIndex], reorderedItems[newIndex]] = [reorderedItems[newIndex], reorderedItems[currentIndex]];
      const itemsWithNewOrder = reorderedItems.map((entry, index) => ({ id: entry.id, sort_order: index + 1 }));
      await reorderItems(venueId, item.categoryId, itemsWithNewOrder, getAccessTokenSilently);

      const updatedItems = items.map((entry) => {
        const newOrder = itemsWithNewOrder.find((candidate) => candidate.id === entry.id);
        return newOrder ? { ...entry, sort_order: newOrder.sort_order } : entry;
      });

      setItems(updatedItems);
    } catch (error) {
      console.error('Failed to move item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveCategory = async (category, direction) => {
    const currentIndex = menuCategories.findIndex((entry) => entry.id === category.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= menuCategories.length) {
      return;
    }

    try {
      setLoading(true);
      const reorderedCategories = Array.from(menuCategories);
      [reorderedCategories[currentIndex], reorderedCategories[newIndex]] = [reorderedCategories[newIndex], reorderedCategories[currentIndex]];
      const categoriesWithNewOrder = reorderedCategories.map((entry, index) => ({ id: entry.id, sort_order: index + 1 }));
      await reorderCategories(venueId, categoriesWithNewOrder, getAccessTokenSilently);

      const updatedCategories = categories.map((entry) => {
        const newOrder = categoriesWithNewOrder.find((candidate) => candidate.id === entry.id);
        return newOrder ? { ...entry, sort_order: newOrder.sort_order } : entry;
      });

      setCategories(updatedCategories);
    } catch (error) {
      console.error('Failed to move category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploadSubmit = async () => {
    if (!imageFile || !currentItem) {
      return;
    }

    setLoading(true);
    setIsUploadModalVisible(false);

    try {
      setNotice(null);
      const imageUrl = await uploadImageWithCleanup(imageFile, venueId, currentItem.imageUrl || null, undefined, currentItem.id);

      if (!imageUrl) {
        throw new Error('Failed to upload image');
      }

      const updated = await updateItem(venueId, { ...currentItem, imageUrl }, getAccessTokenSilently);
      setItems((previousItems) => previousItems.map((item) => (item.id === updated.id ? updated : item)));
      setNotice({
        tone: 'success',
        title: 'Görsel güncellendi',
        message: `${updated.name} için yeni görsel kaydedildi.`,
      });
    } catch (error) {
      console.error('Error uploading image:', error.message);
      setNotice({
        tone: 'error',
        title: 'Görsel yüklenemedi',
        message: error.message || 'Ürün görseli yüklenemedi.',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return faClock;
      case 'active':
        return faEye;
      case 'archived':
        return faArchive;
      default:
        return faEye;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft':
        return 'Taslak';
      case 'active':
        return 'Aktif';
      case 'archived':
        return 'Arşiv';
      default:
        return 'Aktif';
    }
  };

  const getAreaName = (areaId) => {
    if (!areaId) {
      return 'Tüm alanlar';
    }

    const area = areas.find((entry) => entry.id === areaId);
    return area ? area.name : 'Alan atanmadı';
  };

  const metrics = [
    {
      key: 'active',
      label: 'Aktif',
      value: getItemCountByStatus('active'),
      helper: `${getUnavailableActiveCount()} ürün geçici kapalı`,
      active: activeStatusFilter === 'active',
      onClick: () => setActiveStatusFilter('active'),
    },
    {
      key: 'draft',
      label: 'Taslak',
      value: getItemCountByStatus('draft'),
      helper: 'Henüz yayına alınmayan ürünler',
      active: activeStatusFilter === 'draft',
      onClick: () => setActiveStatusFilter('draft'),
    },
    {
      key: 'archived',
      label: 'Arşiv',
      value: getItemCountByStatus('archived'),
      helper: 'Listeden kaldırılmış geçmiş ürünler',
      active: activeStatusFilter === 'archived',
      onClick: () => setActiveStatusFilter('archived'),
    },
  ];

  const renderItemRow = (item, index, groupItems, groupLabel, isUncategorized = false) => {
    const priceLabel = item.price || item.price === 0 ? `₺${item.price}` : 'Belirlenmedi';

    return (
      <article key={item.id} className={`admin-management-row items-row items-row-${item.status} ${isUncategorized ? 'items-row-uncategorized' : ''}`}>
        <div className="admin-management-leading items-row-leading">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt={item.name} className="admin-thumbnail items-thumbnail" />
          ) : (
            <div className="admin-thumbnail-placeholder">
              <FontAwesomeIcon icon={faImage} />
              <span>Görsel yok</span>
            </div>
        )}
        <div className="admin-management-main">
            <h3 className="admin-management-title">{item.name}</h3>
            {item.description ? <p className="admin-management-subtitle">{item.description}</p> : null}
            <div className="items-price-row">
              <span className="items-price-tag">{priceLabel}</span>
            </div>
            <div className="admin-chip-row items-supporting-chip-row">
              <span className={`admin-chip items-status-chip status-${item.status}`}>
                <FontAwesomeIcon icon={getStatusIcon(item.status)} />
                {getStatusText(item.status)}
              </span>
              <span className={`admin-chip ${item.isAvailable === false ? 'admin-chip-warning' : 'admin-chip-accent'}`}>
                {item.isAvailable === false ? 'Satışa Kapalı' : 'Siparişe Açık'}
              </span>
              <span className="admin-chip admin-chip-muted">{groupLabel}</span>
              <span className={`admin-chip ${item.imageUrl ? 'admin-chip-accent' : 'admin-chip-warning'}`}>
                {item.imageUrl ? 'Görsel hazır' : 'Görsel eksik'}
              </span>
              <span className="admin-chip admin-chip-muted">{getAreaName(item.areaId)}</span>
            </div>
            {isUncategorized ? (
              <div className="admin-muted-note items-inline-note">Bu ürün müşteri görünümünde yayınlanmadan önce bir kategoriye taşınmalıdır.</div>
            ) : null}
          </div>
        </div>

        <div className="admin-management-actions">
          <div className="admin-reorder-cluster items-inline-reorder" aria-label={`${item.name} sıralama kontrolleri`}>
            <div className="admin-reorder-buttons">
              <button
                type="button"
                className="admin-reorder-button"
                onClick={() => handleMoveItem(item, 'up', groupItems)}
                disabled={loading || index === 0}
                title="Ürünü yukarı taşı"
                aria-label={`${item.name} ürününü yukarı taşı`}
              >
                <FontAwesomeIcon icon={faChevronUp} />
              </button>
              <button
                type="button"
                className="admin-reorder-button"
                onClick={() => handleMoveItem(item, 'down', groupItems)}
                disabled={loading || index === groupItems.length - 1}
                title="Ürünü aşağı taşı"
                aria-label={`${item.name} ürününü aşağı taşı`}
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
          </div>

          <div className="admin-action-cluster">
            <button type="button" className="btn btn-secondary" onClick={() => { setCurrentItem(item); setIsEditModalVisible(true); }}>
              <FontAwesomeIcon icon={faEdit} />
              {isUncategorized ? 'Kategori Ata' : 'Düzenle'}
            </button>
            {item.status === 'active' ? (
              <button
                type="button"
                className={item.isAvailable === false ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => handleAvailabilityToggle(item, item.isAvailable === false)}
              >
                <FontAwesomeIcon icon={item.isAvailable === false ? faRotateLeft : faBan} />
                {item.isAvailable === false ? 'Satışa Aç' : 'Satışa Kapat'}
              </button>
            ) : null}
            {item.status === 'draft' ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={isUncategorized}
                title={isUncategorized ? 'Önce kategori seçmelisiniz' : undefined}
                onClick={() => handleStatusChange(item, 'active')}
              >
                <FontAwesomeIcon icon={faEye} />
                Yayınla
              </button>
            ) : null}
            {item.status === 'active' ? (
              <button type="button" className="btn btn-secondary" onClick={() => handleArchiveClick(item)}>
                <FontAwesomeIcon icon={faArchive} />
                Arşivle
              </button>
            ) : null}
            {item.status === 'archived' ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={isUncategorized}
                title={isUncategorized ? 'Önce kategori seçmelisiniz' : undefined}
                onClick={() => handleStatusChange(item, 'active')}
              >
                <FontAwesomeIcon icon={faEye} />
                Geri Al
              </button>
            ) : null}
            {item.status !== 'archived' ? (
              <button type="button" className="btn btn-danger" onClick={() => handleDeleteClick(item)}>
                <FontAwesomeIcon icon={faTrash} />
                Sil
              </button>
            ) : null}
          </div>
        </div>
      </article>
    );
  };

  if (!venueMenu) {
    return (
      <div className="page-container">
        <AdminPageHeader title="Ürün Yönetimi" subtitle="Ürünleri, görselleri ve yayın durumlarını tek akışta yönetin." />
        <AdminEmptyState icon={faLayerGroup} title="Henüz menü bulunamadı" description="Ürün eklemek için önce bir menü oluşturulması gerekiyor." />
      </div>
    );
  }

  const uncategorizedItems = uncategorizedCategory
    ? filteredItems.filter((item) => item.categoryId === uncategorizedCategory.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    : [];

  const visibleCategorySections = menuCategories
    .map((category, categoryIndex) => ({
      category,
      categoryIndex,
      items: filteredItems.filter((item) => item.categoryId === category.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Ürün Yönetimi"
        subtitle={`"${venueMenu.name}" menüsü için ürün, durum, kategori düzeni ve görsel kalitesini yoğun bir yönetim görünümünde yönetin.`}
        actions={(
          <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Yeni Ürün
          </button>
        )}
      />

      {notice ? (
        <AdminNotice
          tone={notice.tone}
          title={notice.title}
          message={notice.message}
          onDismiss={() => setNotice(null)}
          autoDismissMs={notice.tone === 'success' || notice.tone === 'info' ? 5000 : undefined}
        />
      ) : null}

      <AdminMetricStrip items={metrics} />

      <div className="admin-page-stack">
        {loading ? <LoadingSpinner /> : null}

        {menuCategories.length === 0 ? (
          <AdminEmptyState icon={faTag} title="Henüz kategori bulunmuyor" description="Ürün eklemek için önce kategoriler oluşturulması gerekiyor." />
        ) : filteredItems.length === 0 ? (
          <AdminEmptyState
            icon={faUtensils}
            title={
              activeStatusFilter === 'draft'
                ? 'Henüz taslak ürün bulunmuyor'
                : activeStatusFilter === 'active'
                  ? 'Henüz aktif ürün bulunmuyor'
                  : 'Henüz arşivlenmiş ürün bulunmuyor'
            }
            description={
              activeStatusFilter === 'draft'
                ? 'Yeni ürünler başlangıçta taslak olarak oluşturulur.'
                : activeStatusFilter === 'active'
                  ? 'Menünüzde görünen aktif ürün bulunmuyor.'
                  : 'Arşivlenmiş ürün bulunmuyor.'
            }
            action={
              activeStatusFilter !== 'archived' ? (
                <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
                  <FontAwesomeIcon icon={faPlus} />
                  {activeStatusFilter === 'draft' ? 'İlk Taslağı Oluştur' : 'İlk Ürünü Ekle'}
                </button>
              ) : null
            }
          />
        ) : (
          <>
            {uncategorizedItems.length > 0 ? (
              <AdminSectionCard
                title="Kategorilendirilmemiş Ürünler"
                subtitle="Bu ürünler için kategori atamadan müşteri görünümünde güvenli yayın akışı tamamlanmaz."
                actions={<span className="admin-chip admin-chip-warning">{uncategorizedItems.length} ürün</span>}
              >
                <div className="admin-management-list items-management-list">
                  {uncategorizedItems.map((item, index) => renderItemRow(item, index, uncategorizedItems, 'Kategori bekliyor', true))}
                </div>
              </AdminSectionCard>
            ) : null}

            {visibleCategorySections.map(({ category, categoryIndex, items: categoryItems }) => (
              <AdminSectionCard
                key={category.id}
                title={category.name}
                subtitle={`${categoryItems.length} ürün bu başlık altında gösteriliyor.`}
                actions={(
                  <div className="admin-reorder-cluster admin-reorder-cluster-compact" aria-label={`${category.name} kategori sıralama kontrolleri`}>
                    <div className="admin-reorder-buttons">
                      <button
                        type="button"
                        className="admin-reorder-button"
                        onClick={() => handleMoveCategory(category, 'up')}
                        disabled={loading || categoryIndex === 0}
                        title="Kategoriyi yukarı taşı"
                        aria-label={`${category.name} kategorisini yukarı taşı`}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                      <button
                        type="button"
                        className="admin-reorder-button"
                        onClick={() => handleMoveCategory(category, 'down')}
                        disabled={loading || categoryIndex === menuCategories.length - 1}
                        title="Kategoriyi aşağı taşı"
                        aria-label={`${category.name} kategorisini aşağı taşı`}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                    </div>
                  </div>
                )}
              >
                <div className="admin-management-list items-management-list">
                  {categoryItems.map((item, index) => renderItemRow(item, index, categoryItems, category.name))}
                </div>
              </AdminSectionCard>
            ))}
          </>
        )}
      </div>

      <AddItemModal show={isAddModalVisible} onClose={() => setIsAddModalVisible(false)} onItemAdded={handleItemAdded} categories={menuCategories} />
      {currentItem ? (
        <>
          <EditItemModal
            show={isEditModalVisible}
            onClose={() => setIsEditModalVisible(false)}
            item={currentItem}
            categories={allMenuCategories}
            uncategorizedCategory={uncategorizedCategory}
            onItemUpdated={handleItemUpdated}
          />
          <UploadImageModal
            show={isUploadModalVisible}
            onClose={() => setIsUploadModalVisible(false)}
            onImageFileChange={setImageFile}
            onSubmit={handleImageUploadSubmit}
          />
        </>
      ) : null}

      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.name}
        message={`"${itemToDelete?.name}" ürününü silmek istediğinizden emin misiniz?`}
        consequences={['Ürün menüden tamamen kaldırılacak.', 'Ürünle ilgili tüm bilgiler silinecek.', 'Bu işlem geri alınamaz.']}
        loading={loading}
        type="danger"
      />

      <DeleteConfirmationModal
        show={showArchiveConfirmation}
        onClose={() => setShowArchiveConfirmation(false)}
        onConfirm={handleConfirmArchive}
        title="Ürünü Arşivle"
        itemName={itemToArchive?.name}
        message={`"${itemToArchive?.name}" ürününü arşivlemek istediğinizden emin misiniz?`}
        consequences={['Ürün müşteri tarafında gizlenecek.', 'Arşivlenen ürün istenirse geri alınabilir.', 'Ürün bilgileri korunur ve silinmez.']}
        loading={loading}
        type="default"
        isArchive
      />
    </div>
  );
};

export default Items;
